/**
 * Ovládání zálohy jednoho projektu.
 *
 * Do nastavení projektu (orchestrátor) i do Codable se vkládá TATÁŽ komponenta —
 * ovládání zálohy má na obou místech znamenat totéž. Dosud nebylo ani na jednom
 * (v orchestrátoru šlo jen vidět zálohy a udělat rollback, v Codable nic).
 *
 * Zálohy projektu jsou PROVOZNÍ: celý uzel jede jako virtuál v PBS, takže se tu
 * drží krátká retence a hlubší historie zůstává tam.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Loader2, Play, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { mcpCall } from '../lib/mcp';
import { themedConfirm } from '../lib/themed-dialogs';
import { Badge, Button, Card, CardContent } from './ui';

/** Výchozí provozní nastavení nové zálohy projektu. */
const DEFAULT_CRON = '30 3 * * *';
const DEFAULT_RETENTION_COUNT = 7;
const DEFAULT_RETENTION_DAYS = 14;

interface Schedule {
  id: string;
  name?: string;
  target_type?: string;
  target_name?: string;
  enabled?: boolean;
  schedule_config?: { cron?: string } | null;
  retention_count?: number;
  retention_days?: number;
  last_run_at?: string | null;
  last_run_status?: string | null;
}

/** Volání MCP — hostitel může dodat node-aware variantu (orchestrátor). */
type McpCaller = <T>(
  tool: string,
  args?: Record<string, unknown>,
) => Promise<T>;

export interface ProjectBackupCardProps {
  /** Kanonická identita projektu (RFC UUID). */
  projectId: string;
  /** Titulek sekce si volí hostitelská stránka (jinde je jiná typografie). */
  title?: string;
  /**
   * Node-aware volání. Projekt může běžet na jiném uzlu než ten, na který je
   * přihlášené UI — pak by globální `mcpCall` sáhl na špatný uzel a ovládal
   * cizí plán zálohy. Orchestrátor proto předává vlastní `useOrchCall`.
   */
  call?: McpCaller;
}

export function ProjectBackupCard(
  { projectId, title, call }: ProjectBackupCardProps,
) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const mcp: McpCaller = call ?? ((tool, args) => mcpCall(tool, args ?? {}));
  const [count, setCount] = useState<number | null>(null);
  const [days, setDays] = useState<number | null>(null);

  const q = useQuery({
    queryKey: ['project-backup-schedule', projectId],
    queryFn: async () => {
      const res = await mcp<{ schedules?: Schedule[] }>(
        'toolbox.backup_scheduler_list',
        {},
      );
      // list neumí filtrovat podle target_name, takže se vybírá tady.
      return (res.schedules ?? []).find((s) =>
        s.target_type === 'project' && s.target_name === projectId
      ) ?? null;
    },
    enabled: !!projectId,
  });

  const schedule = q.data ?? null;
  const retentionCount = count ?? schedule?.retention_count ??
    DEFAULT_RETENTION_COUNT;
  const retentionDays = days ?? schedule?.retention_days ??
    DEFAULT_RETENTION_DAYS;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['project-backup-schedule', projectId] });
    qc.invalidateQueries({ queryKey: ['backups'] });
  };

  const createMut = useMutation({
    mutationFn: () =>
      mcp('toolbox.backup_scheduler_create', {
        name: t('backup.projectScheduleName', { project: projectId }),
        description: t('backup.projectScheduleDescription'),
        target_type: 'project',
        target_name: projectId,
        backup_type: 'full',
        schedule_type: 'cron',
        schedule_config: { cron: DEFAULT_CRON },
        retention_count: DEFAULT_RETENTION_COUNT,
        retention_days: DEFAULT_RETENTION_DAYS,
      }),
    onSuccess: () => {
      toast.success(t('backup.projectScheduleCreated'));
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMut = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      mcp('toolbox.backup_scheduler_update', { id: schedule?.id, ...patch }),
    onSuccess: () => {
      toast.success(t('backup.projectScheduleSaved'));
      setCount(null);
      setDays(null);
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: () => mcp('toolbox.backup_scheduler_delete', { id: schedule?.id }),
    onSuccess: () => {
      toast.success(t('backup.projectScheduleDeleted'));
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const runMut = useMutation({
    mutationFn: () => mcp('toolbox.backup_scheduler_run', { id: schedule?.id }),
    onSuccess: () => {
      toast.success(t('backup.projectRunStarted'));
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const busy = createMut.isPending || updateMut.isPending ||
    deleteMut.isPending ||
    runMut.isPending;
  const dirty = (count !== null && count !== schedule?.retention_count) ||
    (days !== null && days !== schedule?.retention_days);

  return (
    <Card>
      <CardContent className='p-4'>
        <div className='flex items-center gap-3 flex-wrap'>
          <div className='text-xs font-semibold text-blue-400 uppercase tracking-wide'>
            {title ?? t('backup.projectBackupTitle')}
          </div>
          {q.isLoading
            ? <Loader2 size={12} className='animate-spin text-gray-500' />
            : schedule
            ? (
              <Badge variant={schedule.enabled ? 'success' : 'default'}>
                {schedule.enabled ? t('backup.projectOn') : t('backup.projectOff')}
              </Badge>
            )
            : <Badge variant='default'>{t('backup.projectNone')}</Badge>}
          {schedule?.last_run_at && (
            <span className='text-[11px] text-gray-500'>
              {t('backup.projectLastRun', {
                at: new Date(schedule.last_run_at).toLocaleString(),
                status: schedule.last_run_status ?? '—',
              })}
            </span>
          )}
        </div>

        {!schedule
          ? (
            <>
              <p className='text-[11px] text-gray-500 mt-2'>
                {t('backup.projectNoneHint')}
              </p>
              <div className='mt-3'>
                <Button
                  size='sm'
                  variant='primary'
                  loading={createMut.isPending}
                  disabled={busy}
                  icon={<Archive size={12} />}
                  onClick={() => createMut.mutate()}
                >
                  {t('backup.projectEnable')}
                </Button>
              </div>
            </>
          )
          : (
            <>
              <p className='text-[11px] text-gray-500 mt-2'>
                {t('backup.projectScheduleHint', {
                  cron: schedule.schedule_config?.cron ?? DEFAULT_CRON,
                })}
              </p>

              <div className='flex items-end gap-3 flex-wrap mt-3'>
                <label className='text-[11px] text-gray-400'>
                  {t('backup.retentionCount')}
                  <input
                    type='number'
                    min={1}
                    value={retentionCount}
                    onChange={(e) => setCount(Number(e.target.value))}
                    className='block w-20 mt-1 bg-gray-950 border border-gray-700 rounded-lg px-2 py-1 text-xs text-white'
                  />
                </label>
                <label className='text-[11px] text-gray-400'>
                  {t('backup.retentionDays')}
                  <input
                    type='number'
                    min={1}
                    value={retentionDays}
                    onChange={(e) => setDays(Number(e.target.value))}
                    className='block w-20 mt-1 bg-gray-950 border border-gray-700 rounded-lg px-2 py-1 text-xs text-white'
                  />
                </label>
                <Button
                  size='sm'
                  variant='secondary'
                  disabled={busy || !dirty}
                  loading={updateMut.isPending}
                  onClick={() =>
                    updateMut.mutate({
                      retention_count: retentionCount,
                      retention_days: retentionDays,
                    })}
                >
                  {t('backup.projectSaveRetention')}
                </Button>
              </div>

              <div className='flex items-center gap-2 flex-wrap mt-3'>
                <Button
                  size='sm'
                  variant={schedule.enabled ? 'secondary' : 'primary'}
                  disabled={busy}
                  onClick={() => updateMut.mutate({ enabled: !schedule.enabled })}
                >
                  {schedule.enabled ? t('backup.projectDisable') : t('backup.projectEnableAgain')}
                </Button>
                <Button
                  size='sm'
                  variant='secondary'
                  disabled={busy}
                  loading={runMut.isPending}
                  icon={<Play size={12} />}
                  onClick={() => runMut.mutate()}
                >
                  {t('backup.projectRunNow')}
                </Button>
                <Button
                  size='sm'
                  variant='danger'
                  disabled={busy}
                  loading={deleteMut.isPending}
                  icon={<Trash2 size={12} />}
                  onClick={async () => {
                    // Plán se maže, zálohy zůstávají — ať to nikdo neplete.
                    if (
                      !(await themedConfirm(t('backup.projectDeleteConfirm')))
                    ) return;
                    deleteMut.mutate();
                  }}
                >
                  {t('backup.projectDeleteSchedule')}
                </Button>
              </div>
            </>
          )}
      </CardContent>
    </Card>
  );
}

export default ProjectBackupCard;
